/**
 * Monitoramento de Dispositivos — base visual compartilhada.
 *
 * Paleta de referência validada (validate_palette.js, claro/escuro):
 *  - categórica em ORDEM FIXA (espécies, séries) — nunca por ranking;
 *  - STATUS reservado para classificação operacional (sempre com rótulo);
 *  - texto de gráfico usa tokens do tema (--muted-foreground), nunca a cor
 *    da série.
 * Nenhuma regra de negócio aqui: classificação, transição, reincidência e
 * risco chegam prontos do backend (fonte única de verdade).
 */
import { useEffect, useRef, useState } from "react";
import { X, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiErrorMessage, inputCls, typeLabel, useHierarchy } from "./hierarchy";

const CATEGORICAL = {
  light: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"],
  dark: ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9", "#e66767"],
};
export const STATUS = { good: "#0ca30c", warning: "#fab219", serious: "#ec835a", critical: "#d03b3b" };
export const NEUTRAL = "#8a8984";
export const AXIS = "hsl(var(--muted-foreground))";
export const GRID = "hsl(var(--border))";

export const CLASS_INFO = {
  NORMAL: { label: "Normal", color: STATUS.good, icon: "●" },
  ATENCAO: { label: "Atenção", color: STATUS.warning, icon: "▲" },
  CRITICO: { label: "Crítico", color: STATUS.critical, icon: "■" },
  CRITICO_OPERACIONAL: { label: "Crítico operacional", color: STATUS.serious, icon: "◆" },
  NEEDS_REVIEW: { label: "Revisar status", color: NEUTRAL, icon: "?" },
};
export const TRANSITION_INFO = {
  PIORA: { label: "Piora", color: STATUS.critical, icon: "↑" },
  ESTAVEL: { label: "Estável", color: NEUTRAL, icon: "→" },
  MELHORA: { label: "Melhora", color: STATUS.good, icon: "↓" },
  PRIMEIRA_LEITURA: { label: "Primeira leitura", color: NEUTRAL, icon: "•" },
};
export const RISK_INFO = {
  CRITICO: { label: "Crítico", color: STATUS.critical },
  ALTO: { label: "Alto", color: STATUS.serious },
  MODERADO: { label: "Moderado", color: STATUS.warning },
  BAIXO: { label: "Baixo", color: STATUS.good },
  HISTORICO_INSUFICIENTE: { label: "Histórico insuficiente", color: NEUTRAL },
};

export function useIsDark() {
  const get = () => document.documentElement.classList.contains("dark");
  const [dark, setDark] = useState(get);
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(get()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

/** Cor categórica FIXA por entidade (ordem estável pela lista completa). */
export function useCategorical(keys) {
  const dark = useIsDark();
  const ramp = CATEGORICAL[dark ? "dark" : "light"];
  const map = {};
  (keys || []).forEach((k, i) => {
    map[k] = i < ramp.length ? ramp[i] : NEUTRAL; // 9º+ vira "Outros" (cinza)
  });
  return map;
}

export function Badge({ info, children, title }) {
  if (!info) return <span className="text-muted-foreground">—</span>;
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs font-medium whitespace-nowrap"
    >
      <span aria-hidden style={{ color: info.color }}>{info.icon || "●"}</span>
      {children || info.label}
    </span>
  );
}

export const ClassBadge = ({ cls, label, reason }) =>
  <Badge info={CLASS_INFO[cls]} title={reason}>{label || CLASS_INFO[cls]?.label}</Badge>;

export const TransitionBadge = ({ dir, text }) =>
  <Badge info={TRANSITION_INFO[dir]} title={text}>{TRANSITION_INFO[dir]?.label}</Badge>;

export function Card({ title, subtitle, children, right, className = "" }) {
  return (
    <section className={`rounded-lg border border-border bg-card p-4 space-y-3 ${className}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0">
          <h3 className="font-semibold text-sm">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {right && <div className="ml-auto">{right}</div>}
      </div>
      {children}
    </section>
  );
}

export function Empty({ text = "Sem dados" }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-sm text-muted-foreground">
      <Inbox className="h-5 w-5 mb-1 opacity-60" />
      {text}
    </div>
  );
}

export function Stat({ label, value, hint, accent }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        {accent && <span aria-hidden style={{ color: accent }}>●</span>}
        {label}
      </div>
      <div className="text-2xl font-semibold leading-tight">{value ?? "—"}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

export function Delta({ cmp, unit = "", inverse = false }) {
  if (!cmp) return null;
  if (cmp.pct === null || cmp.pct === undefined) {
    return <span className="text-xs text-muted-foreground">sem base anterior</span>;
  }
  const up = cmp.diff > 0;
  const bad = inverse ? !up : up;
  return (
    <span className="text-xs" style={{ color: cmp.diff === 0 ? undefined : bad ? STATUS.critical : STATUS.good }}>
      {up ? "▲" : cmp.diff < 0 ? "▼" : "="} {cmp.diff > 0 ? "+" : ""}{cmp.diff}{unit} ({cmp.pct > 0 ? "+" : ""}{cmp.pct}%)
      <span className="text-muted-foreground"> vs anterior ({cmp.previous}{unit})</span>
    </span>
  );
}

export function ChartTooltip({ active, payload, label, unit = "" }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-sm">
      {label !== undefined && <div className="font-medium mb-1">{label}</div>}
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span aria-hidden style={{ color: p.color || p.fill }}>●</span>
          <span className="text-muted-foreground">{p.name}</span>
          <span className="ml-auto font-medium">{p.value}{unit}</span>
        </div>
      ))}
    </div>
  );
}

export const fmtDay = (iso) => {
  if (!iso) return "—";
  if (/^\d{4}-\d{2}$/.test(iso)) return `${iso.slice(5)}/${iso.slice(0, 4)}`;
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("pt-BR");
};

/* ------------------------------------------------------------------ */
/* Filtros globais — uma linha acima de todos os indicadores          */
/* ------------------------------------------------------------------ */

export const PERIOD_CHOICES = [
  { value: "hoje", label: "Hoje" },
  { value: "7d", label: "7 dias" },
  { value: "30d", label: "30 dias" },
  { value: "mes_atual", label: "Mês atual" },
  { value: "mes_anterior", label: "Mês anterior" },
  { value: "90d", label: "90 dias" },
  { value: "personalizado", label: "Personalizado" },
];

export function filtersToParams(f) {
  const p = {};
  ["client_id", "unit_id", "area_id", "type_key", "species_id", "technician_id"].forEach((k) => {
    if (f[k]) p[k] = f[k];
  });
  if (f.period === "personalizado") {
    if (f.date_from) p.date_from = f.date_from;
    if (f.date_to) p.date_to = f.date_to;
  } else {
    p.period = f.period || "30d";
  }
  return p;
}

const TYPE_CHOICES = ["porta_isca", "trex", "armadilha_luminosa", "armadilha_biologica", "placa_cola",
  "armadilha_mecanica", "catrex"];

export function GlobalFilters({ value, onChange, showTechnician = true }) {
  const f = value;
  const set = (patch) => onChange({ ...f, ...patch });
  const { clients, units, areas } = useHierarchy({ clientId: f.client_id, unitId: f.unit_id });
  const [species, setSpecies] = useState([]);
  const [techs, setTechs] = useState([]);
  useEffect(() => {
    api.get("/device-monitoring/species")
      .then(({ data }) => setSpecies(data.items || []))
      .catch((err) => toast.error(apiErrorMessage(err, "Erro ao carregar espécies.")));
  }, []);
  useEffect(() => {
    if (!showTechnician) return;
    api.get("/device-monitoring/technicians", { params: f.client_id ? { client_id: f.client_id } : {} })
      .then(({ data }) => setTechs(data.items || []))
      .catch(() => setTechs([])); // filtro opcional: perfis sem atribuição não o veem
  }, [f.client_id, showTechnician]);
  const sel = "px-2 py-1.5 rounded-md border border-border bg-background text-sm min-w-0";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="dm-global-filters">
      <select className={sel} value={f.client_id || ""} onChange={(e) => set({ client_id: e.target.value, unit_id: "", area_id: "" })}>
        <option value="">Todos os clientes</option>
        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <select className={sel} value={f.unit_id || ""} disabled={!f.client_id}
        onChange={(e) => set({ unit_id: e.target.value, area_id: "" })}>
        <option value="">Todas as unidades</option>
        {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
      <select className={sel} value={f.area_id || ""} disabled={!f.unit_id} onChange={(e) => set({ area_id: e.target.value })}>
        <option value="">Todas as áreas</option>
        {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      <select className={sel} value={f.type_key || ""} onChange={(e) => set({ type_key: e.target.value })}>
        <option value="">Todos os tipos</option>
        {TYPE_CHOICES.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
      </select>
      <select className={sel} value={f.species_id || ""} onChange={(e) => set({ species_id: e.target.value })}>
        <option value="">Todas as espécies</option>
        {species.map((s) => <option key={s.id} value={s.id}>{s.common_name}</option>)}
      </select>
      {showTechnician && techs.length > 0 && (
        <select className={sel} value={f.technician_id || ""} onChange={(e) => set({ technician_id: e.target.value })}>
          <option value="">Todos os técnicos</option>
          {techs.map((t) => <option key={t.id} value={t.id}>{t.name || t.email}</option>)}
        </select>
      )}
      <select className={sel} value={f.period} onChange={(e) => set({ period: e.target.value })} data-testid="dm-period">
        {PERIOD_CHOICES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
      </select>
      {f.period === "personalizado" && (
        <>
          <input type="date" className={sel} value={f.date_from || ""} onChange={(e) => set({ date_from: e.target.value })} />
          <input type="date" className={sel} value={f.date_to || ""} onChange={(e) => set({ date_to: e.target.value })} />
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Modais: drill-down e detalhe do dispositivo                         */
/* ------------------------------------------------------------------ */

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-4xl rounded-lg border border-border bg-card p-4 space-y-3 mt-8"
        onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose} className="ml-auto p-1 text-muted-foreground hover:text-foreground" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Lista EXATA de registros que compõem um ponto do gráfico. */
export function DrillDown({ request, filters, onClose, onOpenDevice }) {
  const [data, setData] = useState(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    api.get("/device-monitoring/dashboard/drilldown", { params: { ...filtersToParams(filters), ...request.params } })
      .then(({ data: d }) => setData(d))
      .catch((err) => { toast.error(apiErrorMessage(err, "Erro no detalhamento.")); closeRef.current(); });
  }, [request, filters]);
  return (
    <Modal title={request.title} onClose={onClose} testid="dm-drilldown">
      {!data ? <div className="py-8 text-center text-sm text-muted-foreground">Carregando…</div> : (
        <>
          <p className="text-xs text-muted-foreground">
            {data.count} registro(s) · definição: {data.definition}
          </p>
          {data.items.length === 0 ? <Empty /> : (
            <div className="overflow-auto max-h-[60vh] border border-border rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-muted-foreground sticky top-0">
                  <tr>
                    {["Data", "Dispositivo", "Tipo", "Área", "Status", "Classificação", "Transição", "Técnico"].map((h) =>
                      <th key={h} className="text-left py-2 px-2">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.id} className="border-t border-border/50">
                      <td className="py-1.5 px-2 whitespace-nowrap">{fmtDay(r.device_date)}</td>
                      <td className="py-1.5 px-2 font-mono">
                        <button className="underline" onClick={() => onOpenDevice(r.device_id)}>{r.device_code}</button>
                      </td>
                      <td className="py-1.5 px-2">{typeLabel(r.type_key)}</td>
                      <td className="py-1.5 px-2">{r.area_name || "—"}</td>
                      <td className="py-1.5 px-2">{(r.result_status || "").replace(/_/g, " ")}
                        {r.total_weight_g ? ` · ${r.total_weight_g} g` : r.capture ? ` · ${r.total}` : ""}</td>
                      <td className="py-1.5 px-2"><ClassBadge cls={r.classification?.class} /></td>
                      <td className="py-1.5 px-2"><TransitionBadge dir={r.transition_direction} text={r.transition} /></td>
                      <td className="py-1.5 px-2">{r.technician_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

/** Timeline do dispositivo + reincidência + tendência + RISCO PREDITIVO. */
export function DeviceDetail({ deviceId, onClose }) {
  const [data, setData] = useState(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    api.get(`/device-monitoring/analytics/devices/${deviceId}`)
      .then(({ data: d }) => setData(d))
      .catch((err) => { toast.error(apiErrorMessage(err, "Erro ao abrir o dispositivo.")); closeRef.current(); });
  }, [deviceId]);
  const risk = data?.risk;
  return (
    <Modal title={data ? `${data.device.code} — ${typeLabel(data.device.type_key)}` : "Dispositivo"}
      onClose={onClose} testid="dm-device-detail">
      {!data ? <div className="py-8 text-center text-sm text-muted-foreground">Carregando…</div> : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-md border border-border p-3">
              <div className="text-xs text-muted-foreground">Risco preditivo</div>
              {risk.score === null ? (
                <div className="text-sm font-medium">Histórico insuficiente ({risk.n} inspeções)</div>
              ) : (
                <div className="text-2xl font-semibold">
                  {Math.round(risk.score)}/100{" "}
                  <Badge info={RISK_INFO[risk.level]}>{RISK_INFO[risk.level]?.label}</Badge>
                </div>
              )}
              <ul className="mt-1 text-xs text-muted-foreground list-disc pl-4">
                {(risk.reasons || []).map((r) => <li key={r}>{r}</li>)}
              </ul>
            </div>
            <div className="rounded-md border border-border p-3">
              <div className="text-xs text-muted-foreground">Reincidência</div>
              <div className="text-sm font-medium">{data.recurrence.label}</div>
              <div className="text-xs text-muted-foreground">{data.recurrence.text}</div>
            </div>
            <div className="rounded-md border border-border p-3">
              <div className="text-xs text-muted-foreground">Tendência</div>
              <div className="text-sm font-medium">{data.trend.label}</div>
              <div className="text-xs text-muted-foreground">
                {data.trend.confidence ? `confiança ${data.trend.confidence.toLowerCase()} · ${data.trend.n} pontos` : "mínimo 3 inspeções"}
              </div>
            </div>
          </div>
          {risk.factors?.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">Composição do score (auditável)</summary>
              <table className="mt-2 w-full">
                <tbody>
                  {risk.factors.map((f) => (
                    <tr key={f.factor} className="border-t border-border/50">
                      <td className="py-1">{f.text}</td>
                      <td className="py-1 text-right">{f.value} × peso {f.weight}</td>
                      <td className="py-1 text-right font-medium">{f.points} pts</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
          <div>
            <h4 className="text-sm font-medium mb-2">Linha do tempo ({data.inspections} inspeções)</h4>
            {data.timeline.length === 0 ? <Empty text="Sem inspeções registradas" /> : (
              <ol className="relative border-l border-border ml-2 space-y-2">
                {data.timeline.map((e) => (
                  <li key={e.record_id} className="ml-4">
                    <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border-2 border-card"
                      style={{ background: CLASS_INFO[e.classification?.class]?.color || NEUTRAL }} />
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium">{fmtDay(e.date)}</span>
                      <span>{e.status_label}</span>
                      <ClassBadge cls={e.classification?.class} label={e.classification?.label} reason={e.classification?.reason} />
                      <TransitionBadge dir={e.transition_direction} text={e.transition} />
                      <span className="text-xs text-muted-foreground">{e.technician_name}</span>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

export { inputCls };
